"use client"

import { useState, type ReactNode } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { ChevronDown } from "lucide-react"

import { usePortalChrome } from "@/components/site/portal-chrome-context"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import type { PortalNavItem } from "@/lib/portal-content"
import { isNavPageHref, SITE_NAV, SITE_SERVICES } from "@/lib/site-nav"
import { cn } from "@/lib/utils"

function fallbackItems(): PortalNavItem[] {
  return [
    { id: "home", title: SITE_NAV.home.label, href: SITE_NAV.home.href },
    { id: "labels", title: SITE_NAV.labels.label, href: SITE_NAV.labels.href },
    { id: "km", title: SITE_NAV.km.label, href: SITE_NAV.km.href },
    {
      id: "services",
      title: "Сервисы",
      href: "#",
      children: SITE_SERVICES.map((item, index) => ({
        id: `svc-${index}`,
        title: item.label,
        href: item.href,
      })),
    },
    { id: "support", title: SITE_NAV.support.label, href: SITE_NAV.support.href },
  ]
}

function isActiveHref(pathname: string, href: string): boolean {
  if (!isNavPageHref(href)) return false
  if (href === "/") return pathname === "/"
  return pathname === href || pathname.startsWith(`${href}/`)
}

function NavLeaf({ item }: { item: PortalNavItem }) {
  if (!isNavPageHref(item.href)) {
    return (
      <DropdownMenuItem disabled className="opacity-60">
        {item.title}
      </DropdownMenuItem>
    )
  }

  const isHeavy = item.href.startsWith("/labels") || item.href.startsWith("/km")
  return (
    <DropdownMenuItem
      render={
        isHeavy ? (
          <a href={item.href} />
        ) : (
          <Link
            href={item.href}
            prefetch={false}
            target={item.target === "_blank" ? "_blank" : undefined}
          />
        )
      }
    >
      {item.title}
    </DropdownMenuItem>
  )
}

function NavBranch({ item, children }: { item: PortalNavItem; children: PortalNavItem[] }) {
  const pathname = usePathname()
  const sectionActive = children.some((child) => isActiveHref(pathname, child.href))
  const [open, setOpen] = useState(sectionActive)

  return (
    <>
      <button
        type="button"
        className="flex w-full cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="min-w-0 flex-1 truncate">{item.title}</span>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>
      {open
        ? children.map((child) => (
            <DropdownMenuItem
              key={child.id}
              className="pl-6"
              render={<Link href={child.href} prefetch={false} />}
            >
              {child.title}
            </DropdownMenuItem>
          ))
        : null}
    </>
  )
}

/** Пункты главного меню портала — шапка (mobile) и бургер редактора `/labels`. */
export function PortalNavMenuItems({ items }: { items?: PortalNavItem[] }) {
  const { navigation } = usePortalChrome()
  const tree =
    items ??
    (navigation.length > 0 ? navigation : fallbackItems()).filter(
      (item) => item.is_visible !== false,
    )

  const nodes: ReactNode[] = []

  tree.forEach((item, index) => {
    const children = (item.children ?? []).filter((child) => child.is_visible !== false)
    if (index > 0 && children.length > 0) {
      nodes.push(<DropdownMenuSeparator key={`sep-${item.id}`} />)
    }
    nodes.push(
      children.length > 0 ? (
        <NavBranch key={item.id} item={item} children={children} />
      ) : (
        <NavLeaf key={item.id} item={item} />
      ),
    )
    if (children.length > 0 && index < tree.length - 1) {
      nodes.push(<DropdownMenuSeparator key={`sep-after-${item.id}`} />)
    }
  })

  return <>{nodes}</>
}
